import {HttpClient, HttpContext, HttpParams} from '@angular/common/http';
import {Inject, Injectable, inject} from '@angular/core';
import {Observable, firstValueFrom} from 'rxjs';
import {
  ChangePasswordPayload,
  TwoFactorSetupResponse,
  TwoFactorStatus,
} from '@gatewee/types/account-security';
import {TwoFactorAuthService} from './two-factor-auth.service';
import {SKIP_SPINNER} from './tokens/spinner.token';

export type AccountSecurityScope = 'recipient' | 'domain' | 'admin';

@Injectable({
  providedIn: 'root',
})
export class AccountSecurityService {
  private readonly twoFactorAuthService = inject(TwoFactorAuthService);

  constructor(
    private readonly http: HttpClient,
    @Inject('SCOPE') private readonly scope: AccountSecurityScope,
  ) {}

  changePassword(payload: ChangePasswordPayload): Observable<void> {
    return this.http.post<void>(
      `/auth/${this.scope}/change-password`,
      payload,
      {
        context: new HttpContext().set(SKIP_SPINNER, true),
      },
    );
  }

  changePasswordWith2Fa(payload: ChangePasswordPayload): Observable<void> {
    return new Observable<void>(observer => {
      void this.postWith2Fa(`/auth/${this.scope}/change-password`, payload)
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  getTwoFactorStatus(): Observable<TwoFactorStatus> {
    return this.http.get<TwoFactorStatus>(`/auth/${this.scope}/two-factor`, {
      context: new HttpContext().set(SKIP_SPINNER, true),
    });
  }

  enableTwoFactor(): Observable<TwoFactorSetupResponse> {
    return this.http.post<TwoFactorSetupResponse>(
      `/auth/${this.scope}/two-factor/enable`,
      {},
      {
        context: new HttpContext().set(SKIP_SPINNER, true),
      },
    );
  }

  confirmTwoFactor(code: string): Observable<void> {
    return this.http.post<void>(
      `/auth/${this.scope}/two-factor/confirm`,
      {code},
      {
        context: new HttpContext().set(SKIP_SPINNER, true),
      },
    );
  }

  async disableTwoFactor(): Promise<void> {
    await this.postWith2Fa(`/auth/${this.scope}/two-factor/disable`, {});
  }

  private async postWith2Fa(path: string, payload: object): Promise<void> {
    await firstValueFrom(
      this.http.post(
        `/auth/${this.scope}/request-2fa?route=/v1${path}`,
        payload,
        {
          context: new HttpContext().set(SKIP_SPINNER, true),
        },
      ),
    );

    await this.twoFactorAuthService.submit(path, payload, code =>
      this.http.post<void>(path, payload, {
        params: new HttpParams().set('twoFactorCode', code),
        context: new HttpContext().set(SKIP_SPINNER, true),
      }),
    );
  }
}
